import React, { useEffect, useRef } from 'react'
import html2pdf from 'html2pdf.js'
import {
  Download,
  Mail,
  Phone,
  Globe,
  MapPin,
  Briefcase,
  Code,
  ShieldCheck,
  Zap,
  Award,
  GraduationCap,
} from 'lucide-react'

export default function ResumePage() {
  const resumeRef = useRef(null)

  useEffect(() => {
    document.title = 'Omkar More | Digital & NDT Engineer'
  }, [])

  const handleDownload = () => {
    const element = resumeRef.current
    const opt = {
      margin: 0,
      filename: 'Omkar_More_Resume.pdf',
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 4, useCORS: true, letterRendering: true }, // Higher scale for crisp text
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    }
    html2pdf().set(opt).from(element).save()
  }

  return (
    <div className="bg-slate-200 py-6 px-4 pt-20 min-h-screen font-sans print:p-0">
      {/* A4 DOCUMENT */}
      <div
        ref={resumeRef}
        className="mx-auto bg-white shadow-2xl relative print:shadow-none"
        style={{
          width: '210mm',
          height: '297mm',
          color: '#1e293b',
          boxSizing: 'border-box',
        }}
      >
        {/* HEADER */}
        <header className="bg-slate-900 text-white px-8 py-6">
          <div className="flex flex-col gap-2">
            <h1 className="text-4xl font-black tracking-tight leading-none">
              OMKAR MORE
            </h1>

            <p className="text-cyan-400 text-base font-semibold uppercase tracking-wide">
              NDT & Digital Solutions Engineer · UT & PAUT Specialist
            </p>
          </div>

          <div className="flex flex-wrap gap-x-6 gap-y-1 text-[11.5px] font-medium border-t border-slate-700 pt-3 mt-4">
            <span>🎓 PCN Level II – UT / PAUT</span>
            <span>📧 moreomkar972@gmail.com</span>
            <span>📞 +91 9930405472</span>
            <span>📍 India </span>
            <span>🔗 omkarmore.com</span>
          </div>
        </header>

        {/* COMPACT SKILLS BAR */}
        <div className="bg-cyan-800 text-white px-8 py-3 grid grid-cols-4 text-center text-[11px] font-black uppercase tracking-[0.12em]">
          <span>Advanced UT & PAUT</span>
          <span>Automated NDT Reporting</span>
          <span>Industry 4.0 Integration</span>
          <span>Full-Stack SaaS</span>
        </div>

        {/* MAIN CONTENT */}
        <div className="p-10 grid grid-cols-12 gap-10">
          {/* LEFT COLUMN (Sidebar) */}
          <aside
            className="col-span-4 border-r border-slate-100 pr-8 flex flex-col"
            style={{ boxSizing: 'border-box', gap: '20px' }}
          >
            {/* Executive Summary */}
            <section>
              <h3 className="text-[13px] font-black uppercase text-slate-900 border-b-2 border-cyan-600 pb-2 mb-3">
                Executive Summary
              </h3>
              <p className="text-[12.5px] text-black leading-relaxed text-justify">
                Hybrid NDT and Digital Solutions Engineer with hands-on
                expertise in UT & PAUT <br></br>(PCN Level II). Specialized in automating
                complex inspection workflows, developing AI-ready reporting
                platforms, and providing high-level technical consultancy for
                industrial asset integrity.
              </p>
            </section>

            {/* NDT Qualifications */}
            <section>
              <h3 className="text-[13px] font-black uppercase text-slate-900 border-b-2 border-cyan-600 pb-2 mb-3">
                NDT Qualifications
              </h3>
              <div className="flex flex-col" style={{ gap: '8px' }}>
                <div className="bg-slate-50 p-2.5 rounded border-l-2 border-cyan-700">
                  <p className="text-[12px] font-black text-slate-800 uppercase tracking-tight">
                    PCN Level II (ISO 9712)
                  </p>
                  <p className="text-[11px] text-cyan-800 font-bold uppercase mt-0.5">
                    UT • PAUT Specialist
                  </p>
                </div>
                <div className="bg-slate-50 p-2.5 rounded border-l-2 border-slate-400">
                  <p className="text-[12px] font-black text-slate-800 uppercase tracking-tight">
                    NDT Method Certs
                  </p>
                  <p className="text-[11px] text-slate-600 font-bold uppercase mt-0.5">
                    RT • MT • PT • VT
                  </p>
                </div>
                <div className="bg-slate-50 p-2.5 rounded border-l-2 border-slate-400">
                  <p className="text-[12px] font-black text-slate-800 uppercase tracking-tight">
                    Compliance Codes
                  </p>
                  <p className="text-[11px] text-slate-600 font-bold uppercase mt-0.5">
                    ASME Sec V / VIII · API 510
                  </p>
                </div>
              </div>
            </section>

            {/* Technology Stack */}
 <section>
  <h3 className="text-[13px] font-black uppercase text-black border-b-2 border-cyan-600 pb-2 mb-3">
    Technology Stack
  </h3>
  <div className="flex flex-wrap gap-3">
    {[
      'React.js',
      'Node.js',
      'Firebase',
      'AWS',
      'Python',
      'Tailwind',
      'REST API',
    ].map((tech) => (
      <span
        key={tech}
        className="text-[11.5px] font-bold text-slate-700"
      >
        {tech}
      </span>
    ))}
  </div>
</section>


            {/* Academic Profile */}
            <section>
              <h3 className="text-[13px] font-black uppercase text-slate-900 border-b-2 border-cyan-600 pb-2 mb-3">
                Academic Profile
              </h3>
              <p className="font-bold text-[12.5px] text-slate-800">
                B.E. Information Technology
              </p>
              <p className="text-[11.5px] text-slate-500 font-medium italic">
                KC College of Engineering
              </p>
              <p className="text-[11px] text-cyan-700 font-bold mt-1">
                2018 – 2022
              </p>
            </section>
          </aside>

          {/* RIGHT COLUMN (Main Content) */}
          <main className="col-span-8 space-y-8">
            <section>
              <h2 className="text-2xl font-black uppercase mb-5 tracking-tight border-b-2 border-slate-100 pb-2">
                Professional Experience
              </h2>

              <div className="space-y-8">
                <div className="relative pl-6 border-l-2 border-cyan-700">
                  <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-cyan-700 border-4 border-white shadow-sm"></div>
                  <div className="flex justify-between font-black text-[13px] uppercase">
                    <span className="text-slate-800">
                      Focus Inspection Technology
                    </span>
                    <span className="text-cyan-700 font-black">
                      2024 – PRESENT
                    </span>
                  </div>
                  <p className="text-[12.5px] font-bold text-cyan-800 italic mb-3">
                    Lead Digital Transformation & Advanced NDT Engineer
                  </p>
                  <ul className="list-disc ml-5 text-[12.5px] text-black space-y-2.5 leading-normal">
                    <li>
                      <strong>Site Operations (UT/PAUT):</strong> Executed field
                      inspections for high-pressure vessels and refinery assets.
                      Specialized in encoded PAUT scans and manual UT for
                      complex weld geometries.
                    </li>
                    <li>
                      <strong>Technical Advisory:</strong> Advised Tier-1
                      clients on Olympus OmniScan MX2, X3, and X4 procurement,
                      calibration, and deployment; developed and optimized PAUT
                      techniques including probe/wedge selection, focal laws,
                      scan plans, and procedure qualification to ensure
                      compliant and reliable inspections.
                    </li>

                    <li>
                      <strong>Training & Guidance:</strong> Provided technical
                      training and mentorship to UT and PAUT Level I and Level
                      II technicians covering inspection fundamentals,
                      calibration, scan execution, and data interpretation in
                      line with applicable NDT standards.
                    </li>
                  </ul>
                </div>

<div className="relative pl-6 border-l-2 border-slate-200">
  <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-slate-300 border-4 border-white shadow-sm"></div>
  <div className="flex justify-between font-black text-[13px] uppercase">
    <span className="text-slate-800">
      Godrej & Boyce Mfg. Co. Ltd.
    </span>
    <span className="text-slate-400">2022 – 2024</span>
  </div>
  <p className="text-[12.5px] font-bold text-slate-500 italic mb-3">
    Project Engineer – QA/QC & IT Integration
  </p>
  <ul className="list-disc ml-5 text-[12.5px] text-black space-y-2 leading-normal">
    <li>
      Built internal welding project dashboards for real-time monitoring, KPI tracking, and performance reporting.
    </li>
    <li>
      Integrated QA/QC inspection data into ERP systems to streamline reporting and reduce manual errors.
    </li>
    <li>
      Automated generation of compliance and inspection reports, improving operational efficiency by 25%.
    </li>
    <li>
      Collaborated with production and engineering teams to implement data-driven quality control measures.
    </li>
  </ul>
</div>

              </div>
            </section>
<section>
  <h2 className="text-2xl font-black uppercase mb-3 tracking-tight border-b-2 border-slate-100 pb-2">
    Enterprise SaaS & Innovation
  </h2>

  <div className="space-y-2">
    {/* NEW PROJECT: EVALNDT */}
    <div>
      <div className="flex justify-between items-center mb-1">
        <h4 className="font-black text-[12px] text-cyan-600 uppercase">
          EvalNDT Platform
        </h4>
        <span className="text-[9px] text-cyan-700 font-bold uppercase px-1 py-0.5">
          Advanced  PAUT Reporting
        </span>
      </div>
<p className="text-[12.5px] text-slate-800 leading-relaxed">
  Developed a web-based platform for <strong>Advanced PAUT Reporting</strong>, enabling automated inspection file analysis and standardized NDT report generation, significantly reducing post-inspection processing time. <br />
  <span className="text-cyan-600 font-bold">
    URL: https://evalndt.com
  </span>
</p>

    </div>
  </div>
</section>

          </main>
        </div>
      </div>

      {/* ACTION BAR */}
      <div className="max-w-[210mm] pt-10 mx-auto flex justify-center print:hidden">
        <button
          onClick={handleDownload}
          className="flex items-center gap-2 bg-cyan-700 text-white px-6 py-3 rounded-lg font-bold hover:bg-cyan-800 shadow-lg transition-all active:scale-95"
        >
          <Download size={20} /> DOWNLOAD PDF
        </button>
      </div>
    </div>
  )
}
